// Engangs-reset for owner-konto. Krever hemmelig token i URL-en.
// Rute: /eier-reset?token=<secret>
// Fjernes fra kode etter bruk.
import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Shield, Loader2, CheckCircle } from "lucide-react";
import api from "../lib/api";

export default function EierReset() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) return setError("Passord må være minst 8 tegn");
    if (newPassword !== confirm) return setError("Passordene matcher ikke");
    if (!newEmail.includes("@")) return setError("Ugyldig e-post");
    if (!token) return setError("Mangler token i URL — bruk hele lenken du fikk");

    setBusy(true);
    try {
      const r = await api.post("/auth/owner-reset", {
        token,
        new_password: newPassword,
        new_email: newEmail.trim().toLowerCase(),
      });
      if (r.data?.ok) setDone(true);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Reset feilet";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--noir)] text-[var(--cream)] flex items-center justify-center p-4">
      <div className="max-w-md w-full border border-white/10 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-[var(--magenta)]" />
          <span className="font-meta text-xs tracking-[0.2em] text-[var(--cream-soft)]">
            NORSK EROTIKK · EIER-RESET
          </span>
        </div>

        {done ? (
          <>
            <CheckCircle className="w-12 h-12 text-emerald-500 mb-3" />
            <h1 className="font-editorial text-2xl mb-2">Ferdig</h1>
            <p className="text-sm text-[var(--cream-soft)] mb-4">
              Owner-kontoen er oppdatert med nytt passord og ny e-post. Du kan nå
              logge inn på vanlig måte.
            </p>
            <p className="text-xs text-[var(--cream-faint)] mb-4">
              <b>Viktig:</b> be Emergent-agenten om å fjerne token-koden etter du er inne,
              så ingen andre kan bruke denne siden.
            </p>
            <Link
              to="/logg-inn"
              className="block text-center bg-[var(--magenta)] text-white py-3 text-sm font-meta tracking-[0.15em]"
              data-testid="eier-reset-to-login"
            >
              GÅ TIL INNLOGGING
            </Link>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <h1 className="font-editorial text-2xl mb-2">Sett nytt passord</h1>
            {!token && (
              <div className="bg-red-500/10 border border-red-500/40 p-3 text-xs text-red-300">
                Mangler token i URL. Bruk hele lenken du fikk fra Emergent-agenten.
              </div>
            )}
            <div>
              <label className="text-xs text-[var(--cream-faint)] block mb-1">Ny e-post (må være ekte)</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
                className="input-editorial w-full text-sm"
                placeholder="din@eksempel.no"
                data-testid="eier-reset-email"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--cream-faint)] block mb-1">Nytt passord (min 8 tegn)</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="input-editorial w-full text-sm"
                data-testid="eier-reset-password"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--cream-faint)] block mb-1">Bekreft passord</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="input-editorial w-full text-sm"
                data-testid="eier-reset-confirm"
              />
            </div>
            {error && (
              <div className="bg-red-500/10 border border-red-500/40 p-2 text-xs text-red-300">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy || !token}
              className="w-full inline-flex items-center justify-center gap-2 bg-[var(--magenta)] text-white py-3 text-sm font-meta tracking-[0.15em] disabled:opacity-50 hover:opacity-90 transition"
              data-testid="eier-reset-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              SETT NYTT PASSORD
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
